import React, { useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@heroui/react";
import { Calendar, Clock, Info } from "@phosphor-icons/react";

interface ManualAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (date: Date) => void;
  candidateName: string;
  isLoading?: boolean;
}

export const ManualAppointmentModal: React.FC<ManualAppointmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  candidateName,
  isLoading = false,
}) => {
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [hours, setHours] = useState<string>("");
  const [minutes, setMinutes] = useState<string>("");
  const [isDateFocused, setIsDateFocused] = useState(false);
  const [isHoursFocused, setIsHoursFocused] = useState(false);
  const [isMinutesFocused, setIsMinutesFocused] = useState(false);

  // Get minimum date (today) in YYYY-MM-DD format
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const handleHoursChange = (value: string) => {
    const num = parseInt(value);
    if (value === "" || (num >= 0 && num <= 23)) {
      setHours(value);
    }
  };

  const handleMinutesChange = (value: string) => {
    const num = parseInt(value);
    if (value === "" || (num >= 0 && num <= 59)) {
      setMinutes(value);
    }
  };

  const handleConfirm = () => {
    if (!selectedDate || !hours || !minutes) {
      return;
    }

    // Pad hours and minutes with leading zeros
    const paddedHours = hours.padStart(2, "0");
    const paddedMinutes = minutes.padStart(2, "0");

    // Combine date and time into a Date object
    const dateTimeString = `${selectedDate}T${paddedHours}:${paddedMinutes}`;
    const appointmentDate = new Date(dateTimeString);

    // Validate that the date is in the future
    if (appointmentDate < new Date()) {
      return;
    }

    onConfirm(appointmentDate);
    handleClose();
  };

  const handleClose = () => {
    setSelectedDate("");
    setHours("");
    setMinutes("");
    setIsDateFocused(false);
    setIsHoursFocused(false);
    setIsMinutesFocused(false);
    onClose();
  };

  const isValid = selectedDate && hours && minutes;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" backdrop="blur">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Calendar
              weight="duotone"
              size={24}
              className="text-moss-primary"
            />
            <span>Afspraak inplannen</span>
          </div>
          <p className="text-sm font-normal text-neutral-600">
            {candidateName}
          </p>
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            {/* Info Banner */}
            <div className="flex items-start gap-3 rounded-lg bg-blue-50 p-3">
              <Info
                weight="duotone"
                size={20}
                className="mt-0.5 flex-shrink-0 text-blue-600"
              />
              <p className="text-sm text-blue-900">
                Het inboeken van deze afspraak stuurt een agenda uitnodiging
                naar de kandidaat en de bezichtigingsverantwoordelijke
              </p>
            </div>

            {/* Date Input */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-neutral-700">
                Datum
              </label>
              <div className="group relative">
                {!selectedDate && !isDateFocused && (
                  <div className="pointer-events-none absolute top-1/2 left-10 z-10 -translate-y-1/2 text-base text-neutral-400 transition-opacity">
                    dd/mm/yyyy
                  </div>
                )}
                <div
                  className={`pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 transition-colors ${isDateFocused ? "text-moss-primary" : "text-neutral-400"}`}
                >
                  <Calendar weight="duotone" size={18} />
                </div>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  onFocus={() => setIsDateFocused(true)}
                  onBlur={() => setIsDateFocused(false)}
                  min={getMinDate()}
                  className="focus:ring-moss-primary w-full rounded-lg border border-neutral-300 py-2.5 pr-4 pl-10 text-base transition-all hover:border-neutral-400 focus:border-transparent focus:ring-2 focus:outline-none [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                  style={{
                    colorScheme: "light",
                    color: selectedDate ? "inherit" : "transparent",
                  }}
                />
              </div>
              {selectedDate && (
                <p className="flex items-center gap-1 text-xs text-neutral-500">
                  <Calendar size={12} weight="duotone" />
                  Geselecteerd:{" "}
                  {new Date(selectedDate).toLocaleDateString("nl-BE", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              )}
            </div>

            {/* Time Input */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-neutral-700">
                Tijd (24-uurs formaat)
              </label>
              <div className="flex items-center gap-2">
                {/* Hours Input */}
                <div className="flex flex-1 flex-col gap-1">
                  <div className="relative">
                    {!hours && !isHoursFocused && (
                      <div className="pointer-events-none absolute top-1/2 left-10 z-10 -translate-y-1/2 text-base text-neutral-400">
                        UU
                      </div>
                    )}
                    <div
                      className={`pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 transition-colors ${isHoursFocused ? "text-moss-primary" : "text-neutral-400"}`}
                    >
                      <Clock weight="duotone" size={18} />
                    </div>
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={hours}
                      onChange={(e) => handleHoursChange(e.target.value)}
                      onFocus={() => setIsHoursFocused(true)}
                      onBlur={() => setIsHoursFocused(false)}
                      placeholder="UU"
                      className="focus:ring-moss-primary w-full [appearance:textfield] rounded-lg border border-neutral-300 py-2.5 pr-4 pl-10 text-base transition-all hover:border-neutral-400 focus:border-transparent focus:ring-2 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </div>
                  <span className="px-1 text-xs text-neutral-500">0-23</span>
                </div>

                {/* Separator */}
                <span className="mt-[-16px] text-2xl font-medium text-neutral-400">
                  :
                </span>

                {/* Minutes Input */}
                <div className="flex flex-1 flex-col gap-1">
                  <div className="relative">
                    {!minutes && !isMinutesFocused && (
                      <div className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-base text-neutral-400">
                        MM
                      </div>
                    )}
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={minutes}
                      onChange={(e) => handleMinutesChange(e.target.value)}
                      onFocus={() => setIsMinutesFocused(true)}
                      onBlur={() => setIsMinutesFocused(false)}
                      placeholder="MM"
                      className="focus:ring-moss-primary w-full [appearance:textfield] rounded-lg border border-neutral-300 px-4 py-2.5 text-base transition-all hover:border-neutral-400 focus:border-transparent focus:ring-2 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </div>
                  <span className="px-1 text-xs text-neutral-500">0-59</span>
                </div>
              </div>
              {hours && minutes && (
                <p className="flex items-center gap-1 text-xs text-neutral-500">
                  <Clock size={12} weight="duotone" />
                  Geselecteerd: {hours.padStart(2, "0")}:
                  {minutes.padStart(2, "0")}
                </p>
              )}
            </div>

            {/* Validation Message */}
            {selectedDate &&
              hours &&
              minutes &&
              new Date(
                `${selectedDate}T${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`,
              ) < new Date() && (
                <p className="text-danger-600 text-sm">
                  De geselecteerde datum en tijd moet in de toekomst liggen
                </p>
              )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={handleClose} disabled={isLoading}>
            Annuleren
          </Button>
          <Button
            color="success"
            onPress={handleConfirm}
            isLoading={isLoading}
            isDisabled={
              !isValid ||
              ((selectedDate &&
                hours &&
                minutes &&
                new Date(
                  `${selectedDate}T${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`,
                ) < new Date()) as boolean)
            }
          >
            Plan afspraak
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
